import { GetAction } from '@property.com.au/argonaut';
import { notFoundAction, renderAction } from '@rea-argonaut/actions';
import SearchLandingPageScreen from '../screens/SearchLandingPageScreen';
import { createUrqlClient, writeUrqlCacheToExchange } from '../../graphql/urqlClient';
import { getSlugFromPathname } from '../../app-kit/helpers/getSlugFromPathname';
import { fetchSearchLandingPage } from '../graphql/fetchSearchLandingPage';
import {
  isNotFoundErrorPage,
  isSearchLandingPageBySlugErrorPage,
} from './isSearchLandingPageBySlugErrorPage';
import { getHead } from './getHead';
import { Provider as UrqlProvider } from 'urql';
import { UrlContextProvider } from '../../contexts/UrlContext/UrlContext';
import { SearchLandingPageModalContextProvider } from '../contexts/SearchLandingPageModalContext/SearchLandingPageModalContext';
import { StickyHeaderContextProvider } from '../contexts/StickyHeaderContext/StickyHeaderContext';
import { AccountContextProvider } from '../../app-kit/contexts/AccountContext/AccountContext';
import { SearchLandingPageContextProvider } from '../contexts/SearchLandingPageContext/SearchLandingPageContext';

export const getAction: GetAction = async ({ user, url, optimizelyManager }) => {
  const urqlClient = await createUrqlClient();
  const slug = getSlugFromPathname(url.pathname, url.search);
  const { isSignedIn } = user;

  const showAVMData = optimizelyManager.isFeatureEnabled('show_avm_data') || isSignedIn;
  const toggleSuburbCTA = optimizelyManager.isFeatureEnabled('suburb_cta') || isSignedIn;
  const searchLandingPagePromise = fetchSearchLandingPage({
    client: urqlClient,
    slug,
    toggleShowAvmData: showAVMData,
    toggleSuburbCTA,
  });
  const { data, error } = await searchLandingPagePromise;

  if (error?.networkError) {
    throw new Error('Could not successfully connect to Experience API');
  }

  if (!data?.searchLandingPageBySlug) {
    throw Error('No search landing page data returned from Experience API');
  }

  if (isSearchLandingPageBySlugErrorPage(data.searchLandingPageBySlug)) {
    if (isNotFoundErrorPage(data.searchLandingPageBySlug)) {
      return notFoundAction();
    }
    throw Error('No search landing page data returned from Experience API');
  }

  const searchLandingPageBySlug = data.searchLandingPageBySlug;

  await writeUrqlCacheToExchange();

  const { helmet } = searchLandingPageBySlug;

  return renderAction(async () => {
    return {
      getElements: async () => {
        return {
          Screen: (
            <AccountContextProvider isSignedIn={isSignedIn}>
              <UrqlProvider value={urqlClient}>
                <UrlContextProvider url={url}>
                  <SearchLandingPageContextProvider searchLandingPageData={searchLandingPageBySlug}>
                    <SearchLandingPageModalContextProvider>
                      <StickyHeaderContextProvider>
                        <SearchLandingPageScreen user={user} />
                      </StickyHeaderContextProvider>
                    </SearchLandingPageModalContextProvider>
                  </SearchLandingPageContextProvider>
                </UrlContextProvider>
              </UrqlProvider>
            </AccountContextProvider>
          ),
        };
      },
      getHead: async () => (helmet ? getHead(helmet) : ''),
    };
  });
};
